import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex items-center gap-2" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center gap-1.5 px-2 py-1 border border-primary30 rounded bg-primary5 whitespace-nowrap",
  "data-testid": "badge-perfil-padrao"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", { class: "text-sm text-secondary50" }, "Perfil Padrão", -1)

import { ref, computed } from 'vue'
import { mdiFormTextboxLock } from '@mdi/js'

import { RuleBuilderGroup } from '@/components/designSystem/RuleBuilder'
import type { RuleBuilderConfig } from '@/components/designSystem/RuleBuilder'
import ApprovalRoleSelector from './ApprovalRoleSelector.vue'
import ConfirmDeleteRoleModal from '../modals/ConfirmDeleteRoleModal.vue'
import { ensureMandatoryRoles } from '@/utils/approval-flow-transformers'

import {
  LayerOperator,
  MANDATORY_VALIDATION_ROLES,
  ROOT_LAYER_REQUIRES_AND,
  VALIDATION_ROLE_LABELS,
  ValidationRole
} from '@/types/approval-flow.types'
import type { ApprovalGroup, ApprovalRule } from '@/types/approval-flow.types'
import type { RuleBuilderChild } from '@/components/designSystem/RuleBuilder'

interface Props {
  group: ApprovalGroup
  depth?: number
  showErrors?: boolean
}

type DeleteType = 'rule' | 'group' | 'mandatory-rule'

/**
 * Reordenar por drag é a única via que ainda conseguiria tirar os obrigatórios
 * do topo — eles não têm botão de remover e o seletor está desabilitado. Como o
 * peso enviado à API vem da posição, um obrigatório arrastado para o meio
 * reapareceria fora de ordem no reload. Renormalizar na emissão faz a lista
 * voltar sozinha, coerente com o rótulo "Perfil Padrão".
 *
 * Só a raiz é normalizada; `ensureMandatoryRoles` é idempotente, então mudanças
 * vindas de subgrupos passam intactas.
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'ApprovalLayerCard',
  props: {
    group: {},
    depth: { default: 0 },
    showErrors: { type: Boolean, default: false }
  },
  emits: ["update:group", "remove"],
  setup(__props: any, { emit }) {

const props = __props;





function handleUpdate(updated: ApprovalGroup) {
  emit('update:group', props.depth === 0 ? ensureMandatoryRoles(updated) : updated)
}

const showDeleteModal = ref(false)
const deleteType = ref<DeleteType>('rule')
// Papel que o usuário tentou remover, para o modal citá-lo pelo nome.
const blockedRole = ref<ValidationRole | null>(null)
let resolvePromise: ((value: boolean) => void) | null = null

const deleteTitle = computed(() => {
  if (deleteType.value === 'mandatory-rule') return 'Perfil obrigatório'
  return deleteType.value === 'rule' ? 'Excluir regra' : 'Excluir grupo'
})

const deleteDescription = computed(() => {
  if (deleteType.value === 'mandatory-rule') {
    const label = blockedRole.value ? VALIDATION_ROLE_LABELS[blockedRole.value] : 'obrigatório'
    return `O papel "${label}" é obrigatório em todos os fluxos de aprovação e não pode ser removido. Este perfil garante o cumprimento das normas de compliance e auditoria interna.`
  }
  return deleteType.value === 'rule'
    ? 'Tem certeza que deseja excluir essa regra de validação de excepcionalidade? Essa ação não poderá ser revertida'
    : 'Tem certeza que deseja excluir esse grupo de regras de validação de excepcionalidade? Essa ação não poderá ser revertida'
})

function requestConfirmation(type: DeleteType): Promise<boolean> {
  deleteType.value = type
  showDeleteModal.value = true
  return new Promise<boolean>((resolve) => {
    resolvePromise = resolve
  })
}

function handleConfirm() {
  showDeleteModal.value = false
  resolvePromise?.(true)
  resolvePromise = null
}

function handleCancel() {
  showDeleteModal.value = false
  resolvePromise?.(false)
  resolvePromise = null
}

/**
 * Protege a PRIMEIRA ocorrência de cada papel obrigatório no nível raiz.
 *
 * A comparação final é por identidade, não por valor: uma segunda regra com o
 * mesmo papel, ou uma ocorrência dentro de subgrupo, continua removível — só o
 * exemplar que sustenta a regra é intocável.
 */
function mandatoryRoleOf(child: RuleBuilderChild<string, unknown>): ValidationRole | null {
  if (child.type !== 'rule') return null

  const role = MANDATORY_VALIDATION_ROLES.find((mandatory) => child.value === mandatory)
  if (!role) return null

  const firstIndex = props.group.children.findIndex((c) => c.type === 'rule' && c.value === role)
  return firstIndex !== -1 && child === props.group.children[firstIndex] ? role : null
}

function isMandatoryRole(child: RuleBuilderChild<string, unknown>): boolean {
  return mandatoryRoleOf(child) !== null
}

const approvalConfig: RuleBuilderConfig<LayerOperator> = {
  operators: [
    {
      value: LayerOperator.AND,
      label: 'Todos - E',
      description: 'É necessária a aprovação de todos os validadores do grupo'
    },
    {
      value: LayerOperator.OR,
      label: 'Qualquer um - OU',
      description: 'É necessária a aprovação de apenas um membro do grupo'
    }
  ],
  maxDepth: 3,
  ruleLabel: (n) => `Validação ${n}`,
  addRuleLabel: 'Nova regra',
  addGroupLabel: 'Novo grupo',
  defaultOperator: LayerOperator.AND,
  isRuleRemovable: (child) => {
    return !isMandatoryRole(child)
  },
  beforeRemoveRule: (child) => {
    const mandatory = child ? mandatoryRoleOf(child) : null
    if (mandatory) {
      blockedRole.value = mandatory
      return requestConfirmation('mandatory-rule')
    }
    return requestConfirmation('rule')
  },
  beforeRemoveGroup: () => requestConfirmation('group'),
  // Com a flag ligada, a raiz combina os papéis obrigatórios em "E". Sob "OU"
  // bastaria a assinatura de um deles e a exigência conjunta deixaria de valer,
  // então o operador dela não é editável nesse regime — os subgrupos seguem
  // sempre livres. Com a flag desligada, a raiz também fica livre.
  isOperatorLocked: (depth) => ROOT_LAYER_REQUIRES_AND && depth === 0
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(RuleBuilderGroup), {
      group: _ctx.group,
      config: approvalConfig,
      depth: _ctx.depth,
      "operator-label": "Tipo de validação",
      "onUpdate:group": _cache[0] || (_cache[0] = (updated) => handleUpdate(updated as ApprovalGroup)),
      onRemove: _cache[1] || (_cache[1] = ($event: any) => (emit('remove')))
    }, {
      rule: _withCtx(({ rule, update, removable }) => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(ApprovalRoleSelector, {
            "model-value": (rule as ApprovalRule).value,
            disabled: !removable,
            error: _ctx.showErrors && (rule as ApprovalRule).value === null,
            "onUpdate:modelValue": update
          }, null, 8, ["model-value", "disabled", "error", "onUpdate:modelValue"]),
          (!removable)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_component_v_icon, {
                  icon: _unref(mdiFormTextboxLock),
                  size: "16",
                  class: "text-primary30"
                }, null, 8, ["icon"]),
                _hoisted_3
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["group", "depth"]),
    _createVNode(ConfirmDeleteRoleModal, {
      "model-value": showDeleteModal.value,
      title: deleteTitle.value,
      description: deleteDescription.value,
      informational: deleteType.value === 'mandatory-rule',
      onConfirm: handleConfirm,
      onCancel: handleCancel,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = 
        (v) => {
          if (!v) handleCancel()
        }
      )
    }, null, 8, ["model-value", "title", "description", "informational"])
  ]))
}
}

})